import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import { isEmpty, pathOr, replace } from 'ramda';
import {
  AppSummarySectionConfig,
  Labels,
  SdfCardLabels,
  SummaryLabels,
  ItemSummarySection,
} from '../app.models';
import { isSdfCardFlow, PageTitles } from '../config/app-config';
import { LocationFinderService } from '../services/location-finder.service';
import { getTransactionStatus } from '../shared/utils/utils';
import {
  selectConfirmationCode,
  selectReferenceNumber,
} from '../store/transaction/transaction.selectors';
import { ConfirmResponse, TransactionState } from '../store/transaction/types';
import { Clipboard } from '@angular/cdk/clipboard';

@Component({
  selector: 'app-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationComponent implements OnInit {
  isSdfCardFlow: boolean;
  private _summaryData!: TransactionState;
  public currency: any;
  private summaryLabels!: SummaryLabels;
  public bulletStepsWithdraw: any = {
    option: 'defaultsteps',
    return2WalletWithdrawFunds: {
      step1: '1',
      step2: '2',
      step3: '3',
      step4: '4',
    },
    defaultsteps: { step1: '0', step2: '1', step3: '2', step4: '3' },
  };

  @Input() flow: string = 'deposit';
  @Input() locale: string = 'en-US';
  @Input() commitResponse: ConfirmResponse;
  @Input() walletProvider: string = 'VABRANT';
  @Input() isformFreeLocation: boolean = false;
  @Input() isExistingUser: boolean = false;
  @Input() stateOptions: any[];
  @Input() isWalletFeeEnabled: boolean = false;
  implicitUSCanada: string[] = ['USA', 'CAN'];
  sdfCardLabels: SdfCardLabels;
  isEnglishFlow: boolean = true;

  private _selectedLocation!: any;
  transactionStatus: any;
  @Input() public set selectedLocation(selectedLocation: any) {
    this._selectedLocation = selectedLocation;
  }
  public get selectedLocation() {
    return this._selectedLocation;
  }

  public get isImplicitUSCanLocation() {
    return this.implicitUSCanada.includes(
      this.summaryData.sendValidateRequest?.destinationCountryCode!
    );
  }

  public get primaryIdDetails() {
    return this.summaryData.sendValidateRequest?.sender?.primaryIdentification
      ?.typeCode;
  }

  public _labels: any;
  locationLabels: any;

  public dataLayer = ((<any>window).dataLayer = (<any>window).dataLayer || []);

  @Input() public set labels(labels: Labels) {
    this._labels = labels;
    this.isEnglishFlow = labels.stellar.cancelText === 'Cancel';
    this.sdfCardLabels = labels.stellar.sdfcard;
    this.locationLabels = labels?.stellar?.location;
    this.summaryLabels = labels.stellar.summary;
    if (this.summaryData) {
      this._populateCardSections();
    }
  }
  public get labels() {
    return this._labels;
  }

  public _google: any;
  @Input() public set google(map: any) {
    this._google = map;
  }
  public get google() {
    return this._google;
  }

  private _sections: AppSummarySectionConfig[] = [];

  @Input() public set sections(
    sections: AppSummarySectionConfig[] | undefined
  ) {
    this._sections = sections || [];
  }
  public get sections() {
    return this._sections;
  }

  private _exchangeRate!: string;
  @Input() public set exchangeRate(exchangeRate: string | number) {
    if (typeof exchangeRate === 'number') {
      this._exchangeRate = exchangeRate.toFixed(4);
    } else {
      this._exchangeRate = exchangeRate && parseFloat(exchangeRate).toFixed(4);
    }
  }
  public get exchangeRate() {
    return this._exchangeRate;
  }

  @Input() public set summaryData(summaryData) {
    this._summaryData = summaryData;
    this.currency = summaryData?.destinationCountryData?.currency;
  }
  public get summaryData() {
    return this._summaryData;
  }

  items!: any;
  refNumber: string = '';
  confirmationCode: string = '';

  url!: string;

  constructor(
    private store: Store<TransactionState>,
    public locationFinderService: LocationFinderService,
    private clipboard: Clipboard
  ) {
    this.isSdfCardFlow = isSdfCardFlow;
    this.url = document.referrer;
    this.store
      .pipe(select(selectReferenceNumber))
      .subscribe((referenceNumber) => {
        this.refNumber = referenceNumber;
      });

    this.store
      .pipe(select(selectConfirmationCode))
      .subscribe((confirmationCode) => {
        this.confirmationCode = confirmationCode;
      });
  }

  ngOnInit(): void {
    if (!this.isSdfCardFlow) {
      this.getStepsContent();
    } else {
      const trnStatus = getTransactionStatus(
        this.commitResponse.sep24TransactionResponse.status,
        this.labels.stellar.transactionStatus
      );
      this.transactionStatus = trnStatus;
      this.getStepsContent();
      if (this.summaryData) {
        this._populateCardSections();
      }
    }
    if (
      ['LOBSTR', 'DECAF', 'BEANS'].includes(
        this.walletProvider.toLocaleUpperCase()
      )
    ) {
      this.bulletStepsWithdraw.option = 'return2WalletWithdrawFunds';
    }
  }

  getPrimaryIdString() {
    const id = this.primaryIdDetails
      ? this.sdfCardLabels.confirmationPage.nextSteps.idType[
          this.primaryIdDetails
        ]
      : '';
    return replace(
      '%s',
      id,
      this.sdfCardLabels.confirmationPage.nextSteps.paraTwo
    );
  }

  copyCode() {
    let copyContent: string = '';
    if (this.flow === 'deposit' && !this.isformFreeLocation) {
      copyContent = this.commitResponse.confirmationCode || '';
    } else {
      copyContent = this.refNumber || '';
    }
    this.clipboard.copy(copyContent);
  }

  checkForSelectedLocation() {
    if (isEmpty(this.selectedLocation)) {
      const customFieldsLoc = JSON.parse(
        pathOr(
          '',
          ['feeLookupResponse', 'additionalInfos', '0', 'value'],
          this.summaryData
        )
      );
      this.selectedLocation = customFieldsLoc;
    }
  }

  getReceiverFullName() {
    return `${this.summaryData.sendValidateRequest?.sender?.firstName} ${this.summaryData.sendValidateRequest?.sender?.middleName} ${this.summaryData.sendValidateRequest?.sender?.lastName} ${this.summaryData.sendValidateRequest?.sender?.secondLastName}`;
  }

  getExchangeAmountCurrency() {
    return this.flow === 'withdraw'
      ? this.summaryData?.sendValidateResponse?.payoutAmountDetails?.amount
          ?.currencyCode!
      : this.summaryData?.sendValidateResponse?.sendAmountDetails?.amount
          ?.currencyCode!;
  }

  getTotalExchangeAmountCurrency() {
    return this.flow === 'withdraw'
      ? this.summaryData?.sendValidateResponse?.payoutAmountDetails?.totalAmount
          ?.currencyCode!
      : this.summaryData?.sendValidateResponse?.sendAmountDetails?.totalAmount
          ?.currencyCode!;
  }

  getAmount() {
    let amount: string | number;
    amount =
      this.flow === 'withdraw'
        ? this.summaryData?.sendValidateResponse?.sendAmountDetails?.totalAmount
            ?.value!
        : this.summaryData?.sendValidateResponse?.payoutAmountDetails
            ?.totalAmount?.value!;
    if (typeof amount === 'number') {
      amount = amount.toFixed(2);
    } else {
      amount = parseFloat(amount).toFixed(2);
    }
    return parseFloat(amount).toFixed(2);
  }

  getTotalAmount() {
    let amount: string | number;
    amount =
      this.flow === 'withdraw'
        ? this.summaryData?.sendValidateResponse?.payoutAmountDetails
            ?.totalAmount?.value!
        : this.summaryData?.sendValidateResponse?.sendAmountDetails?.totalAmount
            ?.value!;
    if (typeof amount === 'number') {
      amount = amount.toFixed(2);
    } else {
      amount = parseFloat(amount).toFixed(2);
    }
    return parseFloat(amount).toFixed(2);
  }

  getDepositAmountString() {
    return replace(
      '%s',
      this.getAmount(),
      this.sdfCardLabels.confirmationPage.amountAdding
    );
  }

  splitExplicitPurpose(purpose: string) {
    return purpose?.split(',');
  }

  getWithdrawAmountString() {
    return replace(
      '%s',
      this.getAmount(),
      this.sdfCardLabels.confirmationPage.amountWithdraw
    );
  }

  getPaymentMethod() {
    if (this.flow === 'withdraw') {
      return this.walletProvider
        ? `${this.getFormattedWalletName()} ${this.summaryLabels.wallet}`
        : `${this.summaryLabels.wallet}`;
    }
    return this.summaryLabels.cash;
  }

  getReceiveMethod() {
    if (this.flow === 'withdraw') {
      return this.summaryLabels.cashPickup;
    }
    return this.walletProvider
      ? `${this.getFormattedWalletName()} ${this.summaryLabels.wallet}`
      : `${this.summaryLabels.wallet}`;
  }

  getFormattedWalletName() {
    if (this.walletProvider.toLowerCase() === 'vibrant') {
      return 'Vibrant';
    } else if (this.walletProvider.toLowerCase() === 'cinko') {
      return 'CiNKO';
    }
    return this.walletProvider;
  }

  getWithdrawAmount() {
    const value =
      this.flow === 'withdraw'
        ? this.summaryData?.sendValidateResponse?.sendAmountDetails?.totalAmount
            ?.value!
        : this.summaryData?.sendValidateResponse?.sendAmountDetails?.amount
            ?.value!;
    const fxRate =
      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.fxRate || 1;
    if (this.flow === 'withdraw') {
      return Number(value * fxRate).toFixed(2);
    }
    return Number(value).toFixed(2);
  }

  getFees() {
    const value =
      this.summaryData?.sendValidateResponse?.sendAmountDetails?.fees?.value;
    const fxRate =
      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.fxRate || 1;
    if (this.flow === 'withdraw') {
      return Number(value * fxRate).toFixed(2);
    }
    return this.summaryData?.sendValidateResponse?.sendAmountDetails?.fees?.value?.toFixed(
      2
    );
  }

  getPartnerFees(){
    const valFlow = (this.flow === 'deposit' ) ? 
                      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.partnerFees?.value :
                      this.summaryData?.sendValidateResponse?.sendAmountDetails?.partnerFees?.value;
    const fxRateFlow = (this.flow === 'deposit' ) ? 
                      this.summaryData?.sendValidateResponse?.sendAmountDetails?.fxRate :
                      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.fxRate;
    const value = Number(valFlow);
    const fxRate = Number(fxRateFlow);
    return (!isNaN(value) && !isNaN(fxRate))? (value * fxRate).toFixed(2) : "0.00";
  }

  getTaxes() {
    const value =
      this.summaryData?.sendValidateResponse?.sendAmountDetails?.taxes?.value ||
      0;
    const fxRate =
      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.fxRate || 1;
    if (this.flow === 'withdraw') {
      return Number(value * fxRate).toFixed(2);
    }
    return (
      this.summaryData?.sendValidateResponse?.sendAmountDetails?.taxes?.value?.toFixed(
        2
      ) || 0.0
    );
  }

  getReceiveAmount() {
    return (
      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.amount?.value.toFixed(
        2
      ) +
      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.fees?.value.toFixed(
        2
      )
    );
  }

  getTotalUSDC() {
    let totalAmount: string | number;
    totalAmount =
      this.flow === 'withdraw'
        ? this.summaryData?.sendValidateResponse?.payoutAmountDetails
            ?.totalAmount?.value!
        : this.summaryData?.sendValidateResponse?.sendAmountDetails?.totalAmount
            ?.value!;
    if (typeof totalAmount === 'number') {
      totalAmount = totalAmount.toFixed(2);
    } else {
      totalAmount = parseFloat(totalAmount).toFixed(2);
    }
    return parseFloat(totalAmount).toFixed(2);
  }

  getAddressInfo(isLocationDetails: boolean): any {
    let add1 = '';
    let add2 = '';
    if (isLocationDetails) {
      add1 = this.locationFinderService.getFormattedAddress({
        addressLine1: this.selectedLocation?.locationDetails?.addressLine1,
        addressLine2: this.selectedLocation?.locationDetails?.addressLine2,
        addressLine3: this.selectedLocation?.locationDetails?.addressLine3,
      });

      add2 = this.locationFinderService.getFormattedAddress({
        city: this.selectedLocation?.locationDetails?.city,
        state: this.selectedLocation?.locationDetails?.state,
        postalCode: this.selectedLocation?.locationDetails?.postalCode,
        zipCode: this.selectedLocation?.locationDetails?.zipCode,
      });
    } else {
      const userAdd = this.summaryData.sendValidateRequest?.sender?.address;
      add1 = this.locationFinderService.getFormattedAddress({
        addressLine1: userAdd?.line1,
        addressLine2: userAdd?.line2,
      });

      add2 = this.locationFinderService.getFormattedAddress({
        city: userAdd?.city,
        state: userAdd?.countrySubdivisionCode?.slice(-2),
        postalCode: userAdd?.postalCode,
      });
    }

    return `${add1}${add2}`;
  }

  getSelectedIdInfo(id: string | any) {
    return pathOr(
      id,
      ['stellar', 'enumsObj', 'PERSONAL_ID1_TYPE', id],
      this.labels
    );
  }

  getSelectedStateInfo(stateCode: string) {
    return (
      (this.stateOptions &&
        this.stateOptions.find((state) => state.code === stateCode)?.name) ||
      stateCode
    );
  }

  displayFxRate() {
    return this.getExchangeAmountCurrency() === 'USD'
      ? `1 USDC = 1 USD`
      : `1 USDC = 1 USD = ${
          this.exchangeRate
        } ${this.getExchangeAmountCurrency()}`;
  }

  public _populateCardSections(): void {
    if (this.flow === 'deposit') {
      this.checkForSelectedLocation();
    }

    const partnerWalletFee = this.sdfCardLabels?.common?.partnerWalletFee ;
    const walletLabel = partnerWalletFee ? partnerWalletFee : 'Wallet Fees';

    const partnerFees: ItemSummarySection = {
      name: this.isWalletFeeEnabled ? walletLabel.replace("%s1", this.walletProvider.charAt(0).toUpperCase() + this.walletProvider.slice(1).toLowerCase()) : this.sdfCardLabels.common.fees,
      value: this.getPartnerFees() || '0.00',
      sendCurrency: this.flow === 'deposit' ? this.summaryData?.sendValidateResponse?.sendAmountDetails?.totalAmount?.currencyCode! : this.summaryData?.sendValidateResponse?.payoutAmountDetails?.totalAmount?.currencyCode!,
      isBold: false,
      displayMinus: this.flow === 'withdraw' && Number(this.getPartnerFees()) > 0,
    }
    const fees: ItemSummarySection = {
      name: this.sdfCardLabels.common.fees,
      value: this.getFees(),
      sendCurrency: this.flow === 'withdraw' ? this.summaryData?.sendValidateResponse?.payoutAmountDetails?.totalAmount?.currencyCode : this.summaryData?.sendValidateResponse?.sendAmountDetails?.fees?.currencyCode!,
      isBold: false,
      displayMinus: this.flow === 'withdraw' && Number(this.getFees()) > 0,
    }

    this.sections = [
      {
        title: {
          name: this.sdfCardLabels.reviewPage.paymentInformation,
          value: this.displayFxRate(),
          isBold: true,
        },
        items: [
          {
            name:
              this.flow === 'deposit'
                ? this.sdfCardLabels.common.yourPayAmount
                : this.sdfCardLabels.common.yourWithdrawAmount,
            value: this.getWithdrawAmount(),
            sendCurrency:
              this.flow === 'withdraw'
                ? this.summaryData?.sendValidateResponse?.payoutAmountDetails
                    ?.totalAmount?.currencyCode
                : this.getExchangeAmountCurrency(),
            isBold: false,
          },
          {
            name: this.sdfCardLabels.landingPage.payment.payMethod,
            value: this.getPaymentMethod(),
            isBold: false,
          },
          {
            name: this.sdfCardLabels.landingPage.payment.receiveMethod,
            value: this.getReceiveMethod(),
            isBold: false,
          },
          ...[partnerFees]
          ,{
            name: this.sdfCardLabels.common.taxes,
            value: this.getTaxes(),
            sendCurrency:
              this.flow === 'withdraw'
                ? this.summaryData?.sendValidateResponse?.payoutAmountDetails
                    ?.totalAmount?.currencyCode
                : this.summaryData?.sendValidateResponse?.sendAmountDetails
                    ?.taxes?.currencyCode!,
            isBold: false,
            displayMinus:
              this.flow === 'withdraw' && Number(this.getTaxes()) > 0,
          },
        ],
        footer: {
          name:
            this.flow === 'deposit'
              ? this.sdfCardLabels?.common?.totalPayAmount 
              : this.sdfCardLabels?.common?.totalPickupAmount ,
          value: this.getTotalAmount(),
          sendCurrency:
            this.flow === 'withdraw'
              ? this.summaryData?.sendValidateResponse?.payoutAmountDetails
                  ?.totalAmount?.currencyCode
              : this.getTotalExchangeAmountCurrency(),
          isBold: true,
        },
      },
      {
        // title: this.labels?.stellar?.location?.depositLocation,
        title: {
          name: this.sdfCardLabels.reviewPage.paymentLocation,
          isDepositOnlyItem: true,
        },
        items: [
          {
            name: this.selectedLocation?.locationDetails?.name,
            value: this.getAddressInfo(true),
            isBold: true,
          },
        ],
      },
      // {
      //   // title: 'My ID',
      //   title: {
      //     name: 'My ID',
      //     isBold: true,
      //   },
      //   items: [
      //     {
      //       name: 'ID type',
      //       value: this.getSelectedIdInfo(`${this.summaryData.sendValidateRequest?.sender?.primaryIdentification?.typeCode}`),
      //     },
      //     {
      //       name: 'ID number',
      //       value: `${this.summaryData.sendValidateRequest?.sender?.primaryIdentification?.id}`,
      //     },
      //     {
      //       name: 'Country of issue',
      //       value: getCountryConfig(this.summaryData.sendValidateRequest?.sender?.primaryIdentification?.issueCountryCode).name
      //     },
      //     {
      //       name: 'State or province of issue',
      //       value: this.getSelectedStateInfo(`${this.summaryData.sendValidateRequest?.sender?.primaryIdentification?.issueCountrySubdivisionCode}`)
      //     },
      //   ],
      // },
      // {
      //   // title: 'My information',
      //   title: {
      //     name: 'My information',
      //     isBold: true,
      //   },
      //   items: [
      //     {
      //       name: 'Legal name',
      //       value: this.getLegalName(),
      //     },
      //     {
      //       name: 'Date of birth',
      //       value: this.getdateOfBirth(),
      //     },
      //     {
      //       name: 'Address',
      //       value: this.getAddressInfo(false),
      //     },
      //     {
      //       name: this.getCountryLabel(),
      //       value: getCountryConfig(this.summaryData?.sendValidateRequest?.destinationCountryCode).name,
      //     },
      //     {
      //       name: 'Phone number',
      //       value: `+${this.summaryData.sendValidateRequest?.sender?.mobilePhone?.countryDialCode} ${this.summaryData.sendValidateRequest?.sender?.mobilePhone?.number}`
      //     },
      //   ],
      // },
    ];

    if (this.flow === 'withdraw') {
      this._sections = this._sections.filter(
        (section: any) => !section?.title.isDepositOnlyItem
      );
    }
  }

  getLegalName(): string {
    return `${this.summaryData.sendValidateRequest?.sender?.firstName} ${this.summaryData.sendValidateRequest?.sender?.middleName} ${this.summaryData.sendValidateRequest?.sender?.lastName} ${this.summaryData.sendValidateRequest?.sender?.secondLastName}`;
  }

  getdateOfBirth(): string {
    const dobArray =
      this.summaryData.sendValidateRequest?.sender?.dateOfBirth?.split('-');
    if (dobArray) {
      return `${dobArray[1]}/${dobArray[2]}/${dobArray[0]?.slice(-2)}`;
    }
    return '';
  }

  getCountryLabel() {
    return this.summaryLabels.country;
  }

  getRefDetails() {
    return `${this.labels.stellar.confirmation.referenceNumber} ${this.refNumber}`;
  }

  getConfirmationCode() {
    if (this.flow === 'withdraw') {
      const refText =
        this.labels.stellar.confirmation.referenceNumber.split(' ');
      const refWords = refText
        .map((text) => text[0] + text.slice(1).toLowerCase())
        .join(' ');
      // console.log('REF : ', refWords);
      return `${refWords} ${this.refNumber}`;
    }
    return `${this.sdfCardLabels.confirmationPage.accountNumber} ${this.commitResponse.confirmationCode}`;
  }

  handleDone = () => {
    let targetWindow;
    if (window.opener != void 0) {
      targetWindow = window.opener;
    } else if (window.parent != void 0) {
      targetWindow = window.parent;
    } else {
      return;
    }

    if (this.locale && this.locale.substring(0, 2) !== 'en') {
      const moreInfoUrl =
        this.commitResponse.sep24TransactionResponse['more_info_url'];
      if (moreInfoUrl) {
        const more_info_url = moreInfoUrl.replace(
          /(lang=)[^\&]+/,
          '$1' + this.locale
        );
        if (more_info_url) {
          this.commitResponse = {
            ...this.commitResponse,
            sep24TransactionResponse: {
              ...this.commitResponse.sep24TransactionResponse,
              more_info_url,
            },
          };
        }
      }
    }
    // console.log(
    //   'postMessage tx is',
    //   this.commitResponse.sep24TransactionResponse
    // );
    this._analyticsBtnClickEvent(
      'commitTransaction',
      PageTitles.CONFIRMATION_PAGE
    );

    console.log(
      'ZZZ Wallet Provider',
      this.walletProvider
    );
    
    if (this.walletProvider.toLowerCase() === 'belo' && typeof targetWindow?.ReactNativeWebView !== "undefined") {
      targetWindow?.ReactNativeWebView?.postMessage(JSON.stringify(this.commitResponse.sep24TransactionResponse));
      console.log(
        'ZZZ Sent Post Message to Belo',
        this.commitResponse.sep24TransactionResponse
      );
    }

    targetWindow.postMessage(
      {
        transaction: this.commitResponse.sep24TransactionResponse,
      },
      '*'
    );
  };

  getStepsContent() {
    let firstBulletPoint = '';

    if (
      this.walletProvider.toLowerCase() === 'vibrant' ||
      this.walletProvider.toLowerCase() === 'wyre'
    ) {
      firstBulletPoint =
        'To finalize your transaction, please <b>select ok</b> below.';
    } else {
      firstBulletPoint = `To <b>finalize</b> your transaction, please return to your ${this.walletProvider} wallet and submit your payment.`;
    }

    if (this.flow === 'withdraw') {
      this.items = [
        {
          orderId: 1,
          description: firstBulletPoint,
        },
        {
          orderId: 2,
          description: `MoneyGram will send you a <b>reference number</b> for this transaction shortly. You can find the reference in the transactions page of your ${this.walletProvider} wallet.`,
        },
        {
          orderId: 3,
          description:
            'Please bring the <b>reference number</b> and the <b>Photo ID</b> you used for this transaction to the store to pick up your cash.',
        },
      ];
    } else if (this.flow === 'deposit') {
      this.items = [
        {
          orderId: 1,
          description:
            'Go to your chosen <b>MoneyGram location</b> to pay for your purchase within <b>24 hours.</b>',
        },
        {
          orderId: 2,
          description:
            'A MoneyGram agent will help you complete your purchase. Make sure you <b>bring the Photo ID</b> you used for this transaction to the store when you pay for your purchase.',
        },
        {
          orderId: 3,
          description:
            'Check your wallet to see your updated wallet <b>balance</b>.',
        },
      ];
    }
  }

  printPage() {
    window.print();
  }

  private _analyticsBtnClickEvent(event: string, pageTitle: string) {
    const clickInfoObj = {};
    clickInfoObj['event'] = event;
    clickInfoObj['operation'] = this.flow;
    clickInfoObj['transaction-number'] = this.confirmationCode;
    clickInfoObj['reference-number'] = this.refNumber;
    clickInfoObj['operation'] = this.flow;
    clickInfoObj['transaction-Id'] = this.commitResponse?.transactionId;
    clickInfoObj['account-address'] =
      this.commitResponse?.transactionCryptoNetworkInstructions?.accountAddress;
    clickInfoObj['form-free-completion'] = this.isformFreeLocation;
    clickInfoObj['is-existing-user'] = this.isExistingUser;
    clickInfoObj['page-title'] = pageTitle;
    this.dataLayer.push(clickInfoObj);
  }
}
